// src/components/Chat.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { rollDice, apiRequest, rollDiceGuest, postChatMessageGuest, getChatHistoryGuest } from "../api";
import { useGameAnimation } from "./GameAnimationManager";
import PaymentModal from "./PaymentModal";
import "./Chat.css";

interface ChatMessage {
  role: string;    // "system" | "user" | "assistant"
  content: string; // текст сообщения
  created_at?: string; // время создания (опционально)
}

/** Если нужно синхронизировать клетку в App.tsx */
interface ChatProps {
  gameId: number;
  onCellChange?: (newCell: number) => void;
  isGuestMode?: boolean;  // Guest mode flag
  onGuestLimitReached?: (error: any) => void;  // Callback when guest hits limit
  onGuestInteractionUpdate?: (remaining: number) => void;  // Update remaining interactions
  guestInteractionsRemaining?: number;  // Current count of remaining guest interactions
}

/**
 * Компонент "Чат" для "Квантового перекрёстка":
 *  - Загружает историю (GET /games/{gameId}/chat)
 *  - Отправляет user-сообщения (POST /games/{gameId}/chat) → получаем ответ (assistant)
 *  - Скрывает system-сообщения
 *  - Обрезает "Бросок кубика:" (если надо)
 *  - Добавляет кнопки "Бросить кубик" (если последнее сообщение говорит о "броске")
 *  - Визуализирует переносы строк ( \n ) в контенте
 *  - Блокирует кнопки во время отправки/броска (isSendingMessage, isDiceRolling)
 */
const Chat: React.FC<ChatProps> = ({
  gameId,
  onCellChange,
  isGuestMode = false,
  onGuestLimitReached,
  onGuestInteractionUpdate,
  guestInteractionsRemaining = 3
}) => {
  const navigate = useNavigate();
  const { startDiceAnimation, animationState } = useGameAnimation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [userInput, setUserInput] = useState("");
  const [showDiceButton, setShowDiceButton] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // Состояние для анимации/блокировки кнопок
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Payment modal + game state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [gameData, setGameData] = useState<any>(null);
  const [pendingMessage, setPendingMessage] = useState<string>('');
  const [hasAccess, setHasAccess] = useState<boolean>(true);

  // Session expired state
  const [sessionExpired, setSessionExpired] = useState(false);

  // ───────────────────────────────────────────────────────────────────────────
  // 1) Загрузка истории (GET /games/{gameId}/chat)
  // ───────────────────────────────────────────────────────────────────────────
  async function loadHistory() {
    try {
      // Guest mode: используем getChatHistoryGuest без авторизации
      const data: ChatMessage[] = isGuestMode
        ? await getChatHistoryGuest(gameId)
        : await apiRequest(`/games/${gameId}/chat`);
      setMessages(data);
      setSessionExpired(false); // Успешная загрузка - сессия валидна
    } catch (err: any) {
      console.error("Ошибка загрузки чата:", err);

      // Обработка истёкшей сессии
      if (err.status === 401) {
        if (isGuestMode && onGuestLimitReached) {
          // Guest mode: показываем auth modal
          console.warn("Guest limit reached while loading chat history");
          onGuestLimitReached(err);
        } else {
          // Regular mode: session expired
          console.warn("Session expired while loading chat history");
          setSessionExpired(true);
        }
      }
    }
  }

  // При каждом изменении gameId -> загружаем историю + данные игры + статус доступа
  useEffect(() => {
    loadHistory();

    // Guest mode: пропускаем загрузку данных игры и проверку доступа
    // (GuestGamePage управляет состоянием локально)
    if (!isGuestMode) {
      loadGameData();
      checkAccess();
    }
  }, [gameId, isGuestMode]);

  async function loadGameData() {
    try {
      const data = await apiRequest(`/games/${gameId}`);
      setGameData(data);
      setSessionExpired(false); // Успешная загрузка - сессия валидна
    } catch (err: any) {
      console.error("Ошибка загрузки данных игры:", err);

      // Обработка истёкшей сессии
      if (err.status === 401) {
        console.warn("Session expired while loading game data");
        setSessionExpired(true);
      }
    }
  }

  async function checkAccess() {
    try {
      const data = await apiRequest('/me/subscription');
      setHasAccess(data.has_access || false);
      setSessionExpired(false); // Успешная проверка - сессия валидна
    } catch (err: any) {
      console.error("Ошибка проверки доступа:", err);

      // Обработка истёкшей сессии
      if (err.status === 401) {
        console.warn("Session expired while checking access");
        setSessionExpired(true);
        setHasAccess(false);
      } else {
        setHasAccess(false);
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 2) Анализируем последнее assistant-сообщение: есть ли "брос" => кнопка кубика
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const lastAssistant = [...messages].reverse().find(m => m.role === "assistant");
    if (!lastAssistant) {
      setShowDiceButton(false);
      return;
    }
    const text = lastAssistant.content.toLowerCase();
    if (text.includes("брос")) {
      setShowDiceButton(true);
    } else {
      setShowDiceButton(false);
    }
  }, [messages]);

  // ───────────────────────────────────────────────────────────────────────────
  // 3) Отправка сообщения (user) => POST /chat => response (assistant)
  // ───────────────────────────────────────────────────────────────────────────
  async function handleSend() {
    const text = userInput.trim();
    if (!text) return;

    // ПРОВЕРКА ДОСТУПА ДО ОТПРАВКИ
    if (gameData?.request_accepted === 1 && !hasAccess) {
      setPendingMessage(text);
      setShowPaymentModal(true);
      // НЕ очищаем input, НЕ добавляем в messages
      return;
    }

    // Блокируем кнопку + анимация
    setIsSendingMessage(true);
    setRetryCount(0);
    setIsRetrying(false);

    // Добавляем user-сообщение локально
    const newMsg: ChatMessage = { role: "user", content: text };
    setMessages(prev => [...prev, newMsg]);
    setUserInput("");

    try {
      // Guest mode: используем специальную функцию
      let assistantMsg: ChatMessage;
      if (isGuestMode) {
        assistantMsg = await postChatMessageGuest(gameId, "user", text);
      } else {
        assistantMsg = await apiRequest(`/games/${gameId}/chat`, {
          method: "POST",
          body: JSON.stringify(newMsg),
        });
      }

      setMessages(prev => [...prev, assistantMsg]);
      setRetryCount(0); // Сброс счетчика при успехе

      // Guest mode: обновляем счётчик взаимодействий
      if (isGuestMode && onGuestInteractionUpdate) {
        const newCount = Math.max(0, guestInteractionsRemaining - 1);
        onGuestInteractionUpdate(newCount);
      }
    } catch (err: any) {
      console.error("Ошибка отправки сообщения:", err);

      // Guest mode: обработка 401 ошибок (guest limit reached)
      if (isGuestMode && err.status === 401 && onGuestLimitReached) {
        console.warn("Guest limit reached while sending message");
        // Возвращаем сообщение в input
        setUserInput(text);
        setMessages(prev => prev.slice(0, -1)); // Убираем последнее сообщение (user message)
        onGuestLimitReached(err);
        return;
      }

      // Обработка истёкшей сессии (для авторизованных пользователей)
      if (!isGuestMode && err.status === 401) {
        console.warn("Session expired while sending message");
        setSessionExpired(true);
        // Возвращаем сообщение в input
        setUserInput(text);
        setMessages(prev => prev.slice(0, -1)); // Убираем последнее сообщение (user message)
        return; // Показываем модальное окно вместо сообщения об ошибке
      }

      // Проверяем на 402 Payment Required (резервная проверка)
      if (err.status === 402 || err.error === 'payment_required') {
        // Возвращаем сообщение обратно в input, убираем из чата
        setUserInput(text);
        setMessages(prev => prev.slice(0, -1)); // Убираем последнее сообщение (user message)
        setPendingMessage(text);
        setShowPaymentModal(true);
        return; // Не показываем сообщение об ошибке, открываем модальное окно
      }

      // Показываем пользователю сообщение об ошибке
      const errorMsg: ChatMessage = {
        role: "assistant",
        content: getErrorMessage(err, retryCount)
      };
      setMessages(prev => [...prev, errorMsg]);

      // Предлагаем retry для определенных ошибок
      if (shouldOfferRetry(err)) {
        setRetryCount(prev => prev + 1);
        setIsRetrying(true);
      }
    } finally {
      // Разблокируем кнопку
      setIsSendingMessage(false);
    }
  }

  // Функция для retry отправки
  async function handleRetry() {
    if (messages.length < 2) return;
    
    const lastUserMsg = messages[messages.length - 2];
    if (lastUserMsg.role !== "user") return;
    
    setIsRetrying(true);
    
    try {
      const assistantMsg: ChatMessage = await apiRequest(`/games/${gameId}/chat`, {
        method: "POST",
        body: JSON.stringify(lastUserMsg),
      });
      
      // Заменяем последнее сообщение (ошибку) на успешный ответ
      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = assistantMsg;
        return newMessages;
      });
      
      setRetryCount(0);
      setIsRetrying(false);
    } catch (err: any) {
      console.error("Retry failed:", err);

      // Проверяем на 402 Payment Required
      if (err.status === 402 || err.error === 'payment_required') {
        setShowPaymentModal(true);
        return;
      }

      setRetryCount(prev => prev + 1);
    }
  }

  // Функция для определения сообщения об ошибке
  function getErrorMessage(error: any, retryCount: number): string {
    if (retryCount > 0) {
      return `🔮 Попытка ${retryCount + 1} не удалась...

Наш ИИ-наставник все еще испытывает трудности с подключением.

📱 Если проблема повторится, напишите разработчику: [@AlexGo007](https://t.me/AlexGo007)

🧘 Используйте это время для медитации на ваш запрос.`;
    }
    
    if (error.status === 499) {
      return `🔮 Связь с высшими сферами прервалась...

Наш ИИ-наставник временно потерял связь с космическим сознанием.

📱 Если проблема повторится, напишите разработчику: [@AlexGo007](https://t.me/AlexGo007)

🧘 Используйте это время для медитации на ваш запрос.`;
    }
    
    if (error.status >= 500) {
      return `🔮 Временные технические сложности...

Наш ИИ-наставник испытывает трудности с подключением к высшим сферам.

📱 Если проблема повторится, напишите разработчику: [@AlexGo007](https://t.me/AlexGo007)

🧘 Используйте это время для медитации на ваш запрос.`;
    }
    
    return `🔮 Неожиданная ситуация...

Наш ИИ-наставник временно недоступен по техническим причинам.

📱 Обратитесь к разработчику: [@AlexGo007](https://t.me/AlexGo007)

✨ Помните: все препятствия - это часть пути к мудрости.`;
  }

  // Функция для определения, стоит ли предлагать retry
  function shouldOfferRetry(error: any): boolean {
    return retryCount < 2 && (
      error.status === 499 ||
      error.status >= 500 ||
      error.message?.includes('network') ||
      error.message?.includes('fetch')
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 4) "Бросить кубик" с анимацией => rollDice => loadHistory => onCellChange
  // ───────────────────────────────────────────────────────────────────────────
  async function handleRollDiceInChat() {
    try {
      await startDiceAnimation(
        // Callback когда анимация завершена
        async (diceResult) => {
          if (onCellChange && diceResult && typeof diceResult.to_cell === "number") {
            onCellChange(diceResult.to_cell);
          }
          await loadHistory(); // подгружаем новое сообщение ведущего

          // Guest mode: обновляем счётчик взаимодействий
          if (isGuestMode && onGuestInteractionUpdate) {
            // Backend инкрементирует при каждом броске, мы уменьшаем локальный счётчик
            const newCount = Math.max(0, guestInteractionsRemaining - 1);
            onGuestInteractionUpdate(newCount);
          }
        },
        // API вызов который выполняется в фоне
        () => isGuestMode ? rollDiceGuest(gameId) : rollDice(gameId)
      );
    } catch (err: any) {
      console.error("Ошибка при броске кубика:", err);

      // Guest mode: обрабатываем 401 ошибки (guest limit reached)
      if (isGuestMode && err.status === 401 && onGuestLimitReached) {
        onGuestLimitReached(err);
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 5) Функция "обрезки" текста user, если начинается с "Бросок кубика:"
  // ───────────────────────────────────────────────────────────────────────────
  function shortenDiceMessage(original: string): string {
    const lower = original.toLowerCase();
    if (!lower.startsWith("бросок кубика:")) {
      return original;
    }
    const idxVypalo = lower.indexOf("выпало");
    if (idxVypalo < 0) return original;

    const idxDot = original.indexOf(".", idxVypalo);
    if (idxDot < 0) return original;

    return original.substring(0, idxDot + 1);
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 6) Функция для преобразования **текст** в жирный шрифт и \n в переносы строк
  // ───────────────────────────────────────────────────────────────────────────
  function renderTextWithMarkdown(text: string): JSX.Element[] {
    return text.split("\n").map((line, lineIdx) => {
      // Парсим каждую строку на предмет **жирного текста**
      const elements = parseLineForBold(line);
      
      return (
        <p key={lineIdx} style={{ marginBottom: "0.5rem" }}>
          {elements}
        </p>
      );
    });
  }

  function parseLineForBold(line: string): (string | JSX.Element)[] {
    const elements: (string | JSX.Element)[] = [];
    let currentPos = 0;
    let elementKey = 0;

    // Ищем все вхождения **текст**
    const boldPattern = /\*\*(.*?)\*\*/g;
    let match;

    while ((match = boldPattern.exec(line)) !== null) {
      // Добавляем текст до жирного фрагмента
      if (match.index > currentPos) {
        elements.push(line.substring(currentPos, match.index));
      }

      // Добавляем жирный текст
      const boldText = match[1];
      if (boldText) {
        elements.push(
          <strong key={`bold-${elementKey++}`}>{boldText}</strong>
        );
      }

      currentPos = match.index + match[0].length;
    }

    // Добавляем оставшийся текст после последнего жирного фрагмента
    if (currentPos < line.length) {
      elements.push(line.substring(currentPos));
    }

    // Если ничего не нашли, просто возвращаем исходную строку
    if (elements.length === 0) {
      elements.push(line);
    }

    return elements;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // 6) Обработка перед рендером (скрываем system, пропускаем первое user)
  // ───────────────────────────────────────────────────────────────────────────
  function processMessages(msgs: ChatMessage[]): ChatMessage[] {
    // Скрываем system
    const filtered = msgs.filter(m => m.role !== "system");

    // Пропускаем первое user-сообщение (служебное)
    let userCount = 0;
    const step1 = filtered.filter(m => {
      if (m.role === "user") {
        userCount++;
        if (userCount === 1) return false; // скрываем
      }
      return true;
    });

    // Обрезаем "Бросок кубика:"
    return step1.map(m => {
      if (m.role === "user") {
        return { ...m, content: shortenDiceMessage(m.content) };
      }
      return m;
    });
  }

  const visibleMessages = processMessages(messages);

  // ───────────────────────────────────────────────────────────────────────────
  // 7) Рендер
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="chat-container">
      <div className="chat-header">
        <h5>💬 Чат с ведущим</h5>
      </div>

      {visibleMessages.length === 0 ? (
        <div className="chat-empty">
          <div className="chat-empty-icon">💭</div>
          <div className="chat-empty-text">Начните диалог с ведущим</div>
        </div>
      ) : (
        visibleMessages.map((m, idx) => {
          // Разбиваем текст по \n и обрабатываем **жирный текст**
          const contentElements = renderTextWithMarkdown(m.content);

          return (
            <div key={idx} className={`message-bubble ${m.role}`}>
              <div className="message-content">
                {contentElements}
              </div>
            </div>
          );
        })
      )}

      {showDiceButton && (
        <div className="dice-button-container">
          <button
            className={`dice-button ${animationState.isDiceRolling ? "magic-animation" : ""}`}
            onClick={handleRollDiceInChat}
            disabled={animationState.isDiceRolling}
          >
            {animationState.isDiceRolling ? "🎲 Магия в процессе..." : "🎲 Бросить кубик"}
          </button>
        </div>
      )}

      {/* Retry кнопка */}
      {isRetrying && retryCount < 2 && (
        <div className="retry-section">
          <button 
            onClick={handleRetry}
            className="retry-button"
            disabled={isSendingMessage}
          >
            🔄 Попробовать еще раз
          </button>
        </div>
      )}

      <div className="chat-input">
        <div className="input-group">
          <input
            type="text"
            className="form-control"
            placeholder="Напишите сообщение..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend();
            }}
          />
          <button
            className={`send-button ${isSendingMessage ? "magic-animation" : ""}`}
            onClick={handleSend}
            disabled={isSendingMessage}
          >
            {isSendingMessage ? "⏳" : "➤"}
          </button>
        </div>
      </div>

      {/* Session Expired Modal */}
      {sessionExpired && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">⏰ Сессия истекла</h5>
              </div>
              <div className="modal-body">
                <p>Твоя сессия истекла. Чтобы продолжить игру, войди снова.</p>
                <p className="text-muted mb-0">
                  <small>✨ Не волнуйся — твоя игра сохранена и ждёт тебя!</small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary w-100"
                  onClick={() => {
                    // Сохраняем URL игры для возврата
                    localStorage.setItem('redirect_after_login', `/?gameId=${gameId}`);
                    navigate('/auth');
                  }}
                >
                  🔑 Войти снова
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setPendingMessage('');
        }}
        onPaymentSuccess={async () => {
          // После успешной оплаты обновляем статус доступа
          await checkAccess();

          // Автоматически отправляем сохраненное сообщение
          if (pendingMessage.trim()) {
            setUserInput(pendingMessage);
            setPendingMessage('');

            // Небольшая задержка для корректного отображения
            setTimeout(async () => {
              const newMsg: ChatMessage = { role: "user", content: pendingMessage };
              setMessages(prev => [...prev, newMsg]);

              try {
                const assistantMsg: ChatMessage = await apiRequest(`/games/${gameId}/chat`, {
                  method: "POST",
                  body: JSON.stringify(newMsg),
                });
                setMessages(prev => [...prev, assistantMsg]);
                setUserInput('');
              } catch (err) {
                console.error("Ошибка автоотправки:", err);
              }
            }, 500);
          }
        }}
        gameId={gameId}
      />
    </div>
  );
};

export default Chat;
